import {
  PlanEditor,
  SbsComparisonStrategy,
  useCellReviews,
  type BenefitPlanGroup,
  type BuildColumnDefsContext,
  type PlanEditorConfiguration,
  type LocCoverageType,
  type PlanEditorProps,
  type RfpCoverage,
} from "@platform/btx-plan-designer";
import {
  buildCarrierGroupedColumns,
  gatherCarrierLanes,
  SbsCellRenderer,
  useSbsContextMenu,
  type CarrierColumnGroup,
  type PadFillTarget,
  type PlaceholderMaterializeTarget,
} from "@platform/mpx-quotes-grid";
import { CoverageTypeEnum } from "@platform/types";
import { useCallback, useMemo, useRef } from "react";
import { useDialogs } from "@platform/hooks";

import { MovePlanDialog } from "../modals/MovePlanDialog";
import { useMovePlanDialog } from "../hooks/useMovePlanDialog";
import {
  DeletePlanInfo,
  usePlanMenuOptions,
} from "../hooks/usePlanMenuOptions";
import { DeletePlanDialog } from "../modals/DeletePlanDialog";
import { benefitFieldPath } from "../utils/customTextEditedStamp";
import { PLACEHOLDER_PLAN_ID_PREFIX } from "../utils/quotesPlanIds";
import {
  useExtendedFieldKeys,
  useVisibleMpConfigKeys,
} from "../../../../../../../hooks/useVisibleMpConfigKeys";

// Carrier-grouped SBS configuration. The shared PlanEditor is feature-agnostic
// (it only reads `autoHeight`, `stickyHeader`, and `stickyHeaderTop`); every
// field below is consumed by this file's buildColumnDefs closure to drive the
// carrier-grouped Quotes grid.
export interface SbsPlanEditorConfiguration extends PlanEditorConfiguration {
  /** When provided, columns are grouped under carrier-level super-groups. */
  carrierGroups?: CarrierColumnGroup[];
  /** Optional pinned benefits-column header labels for carrier-grouped grids. */
  carrierGroupedBenefitsHeaderLabels?: string[];
  /** Carrier ID that has the "current" plan — its group is locked at position 0. */
  currentCarrierId?: string;
  /**
   * When true, leaf cells in carriers other than `currentCarrierId` use the full
   * edit-mode pipeline instead of being read-only. The current carrier remains
   * read-only and pinned. Used where only proposal plans are editable.
   */
  editableNonCurrentCarriers?: boolean;
  /**
   * Placeholder plan id → materialize target, used to dismiss "awaiting quote"
   * placeholder columns from the carrier header menu (ADR-024).
   */
  placeholderMaterializeByPlanId?: Map<string, PlaceholderMaterializeTarget>;
  /**
   * Lookup from an empty pad plan id (synthesized slot on a real under-quoted
   * group — CD-1612 partial fill) to its fill keys. The carrier header uses it
   * to offer pad dismissal; the host app's save path uses it to route edits.
   */
  padFillByPlanId?: Map<string, PadFillTarget>;
  /**
   * When true, adds a "Version" header tier between the Carrier row and the Plan
   * tier, showing "Current" for the incumbent/requested column or a version chip
   * + received timestamp for every proposal column (ADR-031, quote history).
   */
  showVersionRow?: boolean;
  /**
   * ppx-quote-history: when false, requote/quote-history is deferred — every
   * carrier version still renders as its own column (CD-1730), but with no
   * pending/historical styling and no Replace/Disregard/Restore/Move Plan Group
   * actions. Defaults to true so existing callers keep today's behavior.
   */
  quoteHistoryEnabled?: boolean;
}

export interface PlanEditorContentProps extends PlanEditorProps {
  configuration?: SbsPlanEditorConfiguration;
  ecoRfpId: string;
  selectedCoverageType: CoverageTypeEnum | undefined;
  selectedBpgId: string | undefined;
  benefitPlanGroupsByCoverage: Partial<
    Record<CoverageTypeEnum, BenefitPlanGroup[]>
  >;
  onMoveError: (err: Error) => void;
}

// Owns the Move Plan dialog wiring (useMoveProposalPlanGroup pulls the axios
// instance from PlanDesignerProvider's context) — must live in a component
// rendered as a descendant of that provider, not in Quotes' own body, which
// executes before the provider it returns wraps anything.
export const PlanEditorContent = ({
  ecoRfpId,
  selectedCoverageType,
  selectedBpgId,
  benefitPlanGroupsByCoverage,
  onMoveError,
  ...props
}: PlanEditorContentProps): React.JSX.Element => {
  const getContextMenuItems = useSbsContextMenu();
  const { setEdited } = useCellReviews();
  const { onCoverageChange } = props;
  const { getDialog, closeDialog } = useDialogs();
  const deletePlanDialog = getDialog("deletePlan");

  // Extended (non-v5) fields are the analyst's own; they never reach a carrier,
  // so the Incumbent/Requested column — read-only for every v5 field — accepts
  // edits to these (CD-2301). mpx is the source of truth for which grid keys
  // are extended, shared with BTX Build (CD-2301 stack 3 follow-up).
  const extendedFieldKeys = useExtendedFieldKeys(
    ecoRfpId,
    selectedCoverageType,
  );

  const columnStrategy = useMemo(
    () =>
      new SbsComparisonStrategy(SbsCellRenderer, {
        editableNonCurrentCarriers:
          props.configuration?.editableNonCurrentCarriers,
        editableCurrentCarrierFieldKeys: extendedFieldKeys,
      }),
    [props.configuration?.editableNonCurrentCarriers, extendedFieldKeys],
  );

  const {
    movePlanTarget,
    movePlanMenuConfig,
    handleMovePlanConfirm,
    closeMovePlanDialog,
  } = useMovePlanDialog({
    ecoRfpId,
    currentBenefitPlanGroupId: selectedBpgId,
    benefitPlanGroupsForCoverage: selectedCoverageType
      ? (benefitPlanGroupsByCoverage[selectedCoverageType] ?? [])
      : [],
    onError: onMoveError,
  });

  const { clipboard, copyPlan, pastePlan, openDeletePlanDialog } =
    usePlanMenuOptions({
      ecoRfpId,
      coverageType: selectedCoverageType as LocCoverageType | undefined,
    });

  // The side-by-side shows what the broker's marketing presentation will, so
  // rows their MP configuration hides are dropped here too (CD-386).
  const visibleMpConfigKeys = useVisibleMpConfigKeys(
    ecoRfpId,
    selectedCoverageType,
  );

  const { configuration } = props;
  const buildColumnDefs = useCallback(
    (ctx: BuildColumnDefsContext) =>
      buildCarrierGroupedColumns(
        configuration?.carrierGroups ?? [],
        ctx.coverage,
        ctx.disableNetworkColumns,
        ctx.columnStrategy,
        configuration?.currentCarrierId,
        ctx.planDocumentMap,
        movePlanMenuConfig,
        configuration?.carrierGroupedBenefitsHeaderLabels,
        configuration?.placeholderMaterializeByPlanId,
        configuration?.showVersionRow,
        configuration?.padFillByPlanId,
        copyPlan,
        pastePlan,
        clipboard?.carrierId,
        openDeletePlanDialog,
        configuration?.quoteHistoryEnabled,
      ),
    [
      configuration?.carrierGroups,
      configuration?.currentCarrierId,
      configuration?.carrierGroupedBenefitsHeaderLabels,
      configuration?.placeholderMaterializeByPlanId,
      configuration?.showVersionRow,
      configuration?.padFillByPlanId,
      movePlanMenuConfig,
      copyPlan,
      pastePlan,
      clipboard?.carrierId,
      openDeletePlanDialog,
      configuration?.quoteHistoryEnabled,
    ],
  );

  // ag-grid captures onCellValueChanged (via gridOptions) at grid init and does
  // not refresh it, so this handler closes over the *initial* render's values.
  // Read setEdited through a ref so the stamp uses the live store (same
  // pattern as useProposalPlanBenefitsMutation).
  const setEditedRef = useRef(setEdited);
  setEditedRef.current = setEdited;

  // Persist the "Edited" notation for every cell the analyst changed,
  // whatever populated it (AI extraction, carrier API, or manual entry).
  // onCellValueChanged stamps the edited cells (incl. network) onto the
  // coverage as `_dirtyCells`. A synthetic (pad/placeholder) column has no
  // plan id the review store accepts; the save that materializes it stamps
  // those cells against the real id (Quotes' onNewPlanPersisted).
  const handleCoverageChange = useCallback(
    (updatedCoverage: RfpCoverage) => {
      for (const {
        planId,
        benefitKey,
        network,
      } of updatedCoverage._dirtyCells ?? []) {
        if (planId.startsWith(PLACEHOLDER_PLAN_ID_PREFIX)) continue;
        setEditedRef.current(planId, benefitFieldPath(benefitKey, network));
      }
      onCoverageChange?.(updatedCoverage);
    },
    [onCoverageChange],
  );

  return (
    <>
      <PlanEditor
        {...props}
        quoteHistoryEnabled={configuration?.quoteHistoryEnabled}
        visibleMpConfigKeys={visibleMpConfigKeys}
        onCoverageChange={handleCoverageChange}
        getContextMenuItems={getContextMenuItems}
        CellRenderer={SbsCellRenderer}
        columnStrategy={columnStrategy}
        buildColumnDefs={buildColumnDefs}
        onUserColumnMoved={gatherCarrierLanes}
      />
      {movePlanTarget && selectedCoverageType ? (
        <MovePlanDialog
          open
          productType={movePlanTarget.productType}
          coverageType={selectedCoverageType}
          currentBenefitPlanGroupId={selectedBpgId}
          benefitPlanGroups={
            benefitPlanGroupsByCoverage[selectedCoverageType] ?? []
          }
          onConfirm={handleMovePlanConfirm}
          onClose={closeMovePlanDialog}
        />
      ) : null}
      <DeletePlanDialog
        open={deletePlanDialog?.isOpen || false}
        onCancel={() => closeDialog("deletePlan")}
        planInfo={deletePlanDialog?.data as DeletePlanInfo}
        rfpId={ecoRfpId}
      />
    </>
  );
};
